/* assets/notify-me.js
 * Custom Notify-Me UI + hidden Timesact relay (Option A).
 *
 * Flow:
 *  1. Our Liquid button renders instantly (server-side availability).
 *  2. Click → our custom modal opens immediately.
 *  3. On submit, we relay the email into Timesact's (CSS-hidden) widget:
 *     click their trigger → wait for their input → set email → click their submit
 *     → wait for their success element → show our success state.
 *
 * ─────────────────────────────────────────────────────────────────
 *  ⚠ STEP 0: Replace the four selectors in TA below with the real
 *  ones you captured in DevTools from the Timesact widget.
 * ─────────────────────────────────────────────────────────────────
 */
(function () {
  'use strict';

  // ── Timesact selectors (verified against live widget DOM) ───────
  // Success/error spans ALWAYS exist in the DOM; Timesact signals the
  // result by toggling the `timesact_bis_message_show` class. The
  // compound selectors below only match once that class is applied.
  const TA = {
    trigger:    'button.timesact-button-bis',                            // their BIS button (sets variant context)
    input:      '#timesact_bis_email',                                   // their email field
    submit:     '.timesact_bis_submit_button',                           // their submit button
    success:    '.timesact_bis_message_success.timesact_bis_message_show',
    error:      '.timesact_bis_message_error.timesact_bis_message_show',
    // Spans whose stale `_show` class must be cleared before each attempt:
    messages:   '.timesact_bis_message_success, .timesact_bis_message_error',
    showClass:  'timesact_bis_message_show',
    timeout: 12000                                                        // ms to wait for their widget
  };

  // ── DOM refs ─────────────────────────────────────────────────────
  const modal = document.querySelector('[data-notify-modal]');
  if (!modal) return;

  const form        = modal.querySelector('[data-notify-form]');
  const emailInput  = modal.querySelector('#nm-email');
  const submitBtn   = modal.querySelector('[data-notify-submit]');
  const spinner     = modal.querySelector('.nm-modal__spinner');
  const submitLabel = modal.querySelector('.nm-modal__submit-label');
  const errorEl     = modal.querySelector('[data-notify-error]');
  const titleEl     = modal.querySelector('[data-notify-product-title]');
  const states      = modal.querySelectorAll('[data-notify-state]');

  // ── Helpers ──────────────────────────────────────────────────────
  function waitFor(selector, timeout = TA.timeout) {
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(selector);
      if (existing) return resolve(existing);

      let timer;
      const obs = new MutationObserver(() => {
        const el = document.querySelector(selector);
        if (el) {
          obs.disconnect();
          clearTimeout(timer);
          resolve(el);
        }
      });
      obs.observe(document.documentElement, { childList: true, subtree: true, attributes: true });
      timer = setTimeout(() => {
        obs.disconnect();
        reject(new Error('[notify-me] Timesact element not found: ' + selector));
      }, timeout);
    });
  }

  // Framework-safe value setter (works if their widget is React/Vue-based)
  function setNativeValue(input, value) {
    const setter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype, 'value'
    ).set;
    setter.call(input, value);
    input.dispatchEvent(new Event('input',  { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function showState(name) {
    states.forEach(s => { s.hidden = s.dataset.notifyState !== name; });
  }

  function setLoading(isLoading) {
    submitBtn.disabled = isLoading;
    spinner.hidden = !isLoading;
    submitLabel.textContent = isLoading ? 'Submitting…' : 'Notify me';
  }

  function showError(msg) {
    errorEl.textContent = msg;
    errorEl.hidden = false;
  }

  // ── Modal open/close ─────────────────────────────────────────────
  function openModal(productTitle) {
    if (titleEl) titleEl.textContent = productTitle || document.title;
    showState('form');
    errorEl.hidden = true;
    modal.hidden = false;
    document.body.classList.add('nm-modal-open');
    setTimeout(() => emailInput.focus(), 50);
  }

  function closeModal() {
    modal.hidden = true;
    document.body.classList.remove('nm-modal-open');
  }

  document.addEventListener('click', (e) => {
    // List-layout chip: nested inside a row <a>, so intercept and
    // navigate ourselves instead of letting the row link fire.
    const linkChip = e.target.closest('[data-notify-link]');
    if (linkChip) {
      e.preventDefault();
      e.stopPropagation();
      window.location.href = linkChip.dataset.notifyLink;
      return;
    }

    const trigger = e.target.closest('[data-notify-trigger]');
    if (trigger) {
      e.preventDefault();
      openModal(trigger.dataset.productTitle);
      return;
    }
    if (e.target.closest('[data-notify-close]')) closeModal();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !modal.hidden) closeModal();
    // Keyboard support for the role="link" list chip
    if (e.key === 'Enter') {
      const chip = e.target.closest && e.target.closest('[data-notify-link]');
      if (chip) {
        e.preventDefault();
        e.stopPropagation();
        window.location.href = chip.dataset.notifyLink;
      }
    }
  });

  // ── The Timesact relay ───────────────────────────────────────────
  async function relayToTimesact(email) {
    // 1. Their trigger button — opens their (CSS-hidden) dialog and,
    //    importantly, binds the currently selected variant to the flow.
    const taTrigger = await waitFor(TA.trigger);
    taTrigger.click();

    // 2. Their email input
    const taInput = await waitFor(TA.input);
    setNativeValue(taInput, email);

    // 3. Clear any stale result state from a previous attempt —
    //    the message spans persist and only toggle a `_show` class,
    //    so a leftover class would read as an instant false result.
    document.querySelectorAll(TA.messages).forEach((el) => {
      el.classList.remove(TA.showClass);
    });

    // 4. Their submit
    const taSubmit = await waitFor(TA.submit);
    taSubmit.click();

    // 5. Race their success vs. error message. Whichever gains the
    //    `_show` class first decides the outcome.
    const result = await Promise.race([
      waitFor(TA.success).then((el) => ({ ok: true,  el })),
      waitFor(TA.error).then((el)   => ({ ok: false, el }))
    ]);

    if (!result.ok) {
      // Surface Timesact's own message (e.g. "already subscribed")
      const msg = (result.el.textContent || '').trim();
      throw new Error(msg || 'Subscription was not accepted.');
    }
  }

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    errorEl.hidden = true;

    const email = emailInput.value.trim();
    if (!email || !emailInput.checkValidity()) {
      showError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    try {
      await relayToTimesact(email);
      showState('success');
    } catch (err) {
      console.error(err);
      // Widget failed to load/respond → generic retry message.
      // Timesact rejected the submission → show its actual reason.
      const isLoadFailure = /Timesact element not found/.test(err.message);
      showError(isLoadFailure
        ? 'Something went wrong. Please try again in a moment.'
        : err.message);
    } finally {
      setLoading(false);
    }
  });

  // ── PDP: keep button in sync on variant change ──────────────────
  // NOTE: In Baseline the buy-buttons area is a <data-island> variant
  // fragment that RE-RENDERS server-side on variant change — so the
  // button's data-visible state is already refreshed by the theme.
  // This JS sync is a fallback, and re-queries the DOM on every run
  // because fragment re-renders replace the elements (stale refs).
  function syncButton() {
    const pdpBtn = document.querySelector('.notify-me-btn--pdp');
    if (!pdpBtn) return;

    // Scope lookups to the button's OWN form — pages can contain other
    // /cart/add forms (quick buy, complementary products), and a
    // document-wide query could read the wrong variant and wrongly
    // hide the button.
    const scope = pdpBtn.closest('form[action*="/cart/add"]') || document;
    const variantsScript = scope.querySelector('[data-notify-variants]')
      || document.querySelector('[data-notify-variants]');
    const idInput = scope.querySelector('[name="id"]');
    if (!variantsScript || !idInput) return;

    let variants = [];
    try { variants = JSON.parse(variantsScript.textContent); } catch (_) { return; }

    const v = variants.find(x => String(x.id) === String(idInput.value));
    if (!v) return; // unknown id — leave the server-rendered state alone
    pdpBtn.dataset.visible = (!v.available).toString();
  }

  if (document.querySelector('.notify-me-btn--pdp')) {
    // Baseline updates the hidden [name="id"] input on variant change;
    // the fragment swap happens shortly after — delay covers both.
    document.addEventListener('change', (e) => {
      if (e.target.closest('form[action*="/cart/add"]') || e.target.name === 'id') {
        setTimeout(syncButton, 150);
      }
    });
    syncButton();
  }

  // ── Auto-open when arriving from a collection card (?notify=1) ──
  if (new URLSearchParams(location.search).has('notify')) {
    const t = document.querySelector('[data-notify-trigger]');
    openModal(t ? t.dataset.productTitle : document.title);
  }
})();